import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Filter, Grid, List } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import "./order-a-test.css";
import Loading from "../loading/Loading";

const formatPrice = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "₦0.00";
  }
  let numAmount;
  if (typeof amount === 'string') {
    const cleanAmount = amount.replace(/[₦,]/g, '').replace(/\.00$/, '');
    numAmount = parseFloat(cleanAmount);
  } else {
    numAmount = parseFloat(amount);
  }
  if (isNaN(numAmount)) {
    return "₦0.00";
  }
  return `₦${numAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const OrderATest = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("orderATestViewMode") || "grid";
  });
  const [filterBy, setFilterBy] = useState("category");
  const [currentPage, setCurrentPage] = useState(1);
  const [testsPerPage, setTestsPerPage] = useState(6);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageInput, setPageInput] = useState("1");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [tests, setTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_ENDPOINT = "/wp-json/dbsb/v1/medical-tests";

  // Fetch tests from API
  useEffect(() => {
    const fetchTests = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Check for nonce
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        const response = await fetch(`${API_ENDPOINT}?per_page=100`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(
              "You must be logged in to view tests. Please log in and try again."
            );
          } else if (response.status === 403) {
            throw new Error("You don't have permission to view tests.");
          } else {
            throw new Error("Failed to fetch tests. Please try again.");
          }
        }

        const data = await response.json();

        // Map API data to match component's expected structure
        const mappedTests = data.tests.map((test) => ({
          id: test.id.toString(),
          slug: test.slug,
          name: test.title,
          info: test.excerpt,
          price: formatPrice(test.test_price),
          processingTime: test.processing_time,
          category: test.category?.slug || "uncategorized",
        }));

        setTests(mappedTests);
        setFilteredTests(mappedTests);
      } catch (err) {
        console.error("Error fetching tests:", err);
        setError(err.message);
        setTests([]);
        setFilteredTests([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTests();
  }, []);

  // Save viewMode to localStorage
  useEffect(() => {
    localStorage.setItem("orderATestViewMode", viewMode);
  }, [viewMode]);

  // Filter and sort tests
  useEffect(() => {
    let filtered = [...tests];

    if (filterBy === "category") {
      filtered.sort((a, b) => a.category.localeCompare(b.category));
    } else if (filterBy === "processingTime") {
      filtered.sort((a, b) => {
        const getDays = (time) => {
          const match = time.match(/(\d+)(?:-(\d+))? (?:weeks?|days?)/);
          if (!match) return { min: Infinity, max: Infinity };
          const min = parseInt(match[1]);
          const max = match[2] ? parseInt(match[2]) : min;
          return { min, max };
        };
        const aDays = getDays(a.processingTime);
        const bDays = getDays(b.processingTime);
        return aDays.min - bDays.min || aDays.max - bDays.max;
      });
    } else if (filterBy === "amount") {
      filtered.sort((a, b) => {
        const aPrice = parseFloat(a.price.replace("₦", "").replace(",", ""));
        const bPrice = parseFloat(b.price.replace("₦", "").replace(",", ""));
        return aPrice - bPrice;
      });
    }

    if (searchQuery) {
      filtered = filtered.filter((test) =>
        test.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredTests(filtered);
    setCurrentPage(1);
    setPageInput("1");
  }, [filterBy, searchQuery, tests]);

  // Handle initial load animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const totalTests = filteredTests.length;
  const totalPages = Math.ceil(totalTests / testsPerPage);
  const startIndex = (currentPage - 1) * testsPerPage;
  const endIndex = Math.min(startIndex + testsPerPage, totalTests);
  const currentTests = filteredTests.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setPageInput(page.toString());
    }
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    setPageInput(value);
    const page = parseInt(value);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleTestsPerPageChange = (e) => {
    setTestsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
    setPageInput("1");
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.05,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
    hover: {
      scale: 1.02,
      boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.05)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10,
      },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
    hover: {
      backgroundColor: "rgba(58, 63, 106, 0.02)",
      transition: { duration: 0.2 },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      transition: { duration: 0.2 },
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1 },
    },
  };

  const pageTransition = {
    type: "tween",
    ease: "easeInOut",
    duration: 0.3,
  };

  const headingVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
        delay: 0.1,
      },
    },
  };

  const searchVariants = {
    rest: {
      scale: 1,
      boxShadow: "0px 0px 0px rgba(0, 0, 0, 0)",
    },
    focus: {
      scale: 1.01,
      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 20,
      },
    },
  };

  return (
    <motion.div
      className="order-a-test-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="order-a-test-labs">
        <div className="order-a-test-labs-left">
          <motion.h1
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Order a test
          </motion.h1>
          <motion.p
            className="subtitle"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Request diagnostic tests for your patients with ease.
          </motion.p>
        </div>
        <motion.button
          className="partner-labs-button"
          onClick={() => navigate("/partner-labs")}
        >
          See our partner labs
        </motion.button>
      </div>

      <motion.div
        className="orders-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.5,
          delay: 0.3,
          type: "spring",
          stiffness: 100,
        }}
      >
        <motion.h4
          className="card-subheading"
          variants={headingVariants}
          initial="hidden"
          animate="visible"
        >
          Select the test(s) you want to order
        </motion.h4>

        {isLoading ? (
          <motion.div
            className="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Loading />
          </motion.div>
        ) : error ? (
          <motion.div
            className="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            Error: {error}
          </motion.div>
        ) : tests.length > 0 ? (
          <>
            <motion.div
              className="orders-header"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <motion.div
                className="search-bar"
                variants={searchVariants}
                initial="rest"
                whileFocus="focus"
                whileHover="focus"
              >
                <Search size={20} color="#656E71" />
                <input
                  type="text"
                  placeholder="Search tests by name"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </motion.div>
              <div className="pagination-info">
                <motion.span
                  className="pagination-page-info"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  {startIndex + 1}-{endIndex}
                  <span>/{totalTests} tests</span>
                </motion.span>
                <motion.div
                  className="filter-select"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <svg
                    width="25"
                    height="26"
                    viewBox="0 0 20 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.9406 16.3885C11.9406 16.8969 11.6073 17.5635 11.1823 17.8219L10.0073 18.5802C8.91563 19.2552 7.39896 18.4969 7.39896 17.1469V12.6885C7.39896 12.0969 7.06562 11.3385 6.72396 10.9219L3.52394 7.5552C3.09894 7.1302 2.76562 6.38021 2.76562 5.87188V3.93854C2.76562 2.9302 3.52397 2.17188 4.44897 2.17188H15.5656C16.4906 2.17188 17.249 2.9302 17.249 3.8552V5.7052C17.249 6.3802 16.824 7.22187 16.4073 7.63854"
                      stroke="#31383B"
                      stroke-width="1.25"
                      stroke-miterlimit="10"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M13.3932 14.263C14.866 14.263 16.0599 13.0691 16.0599 11.5964C16.0599 10.1236 14.866 8.92969 13.3932 8.92969C11.9205 8.92969 10.7266 10.1236 10.7266 11.5964C10.7266 13.0691 11.9205 14.263 13.3932 14.263Z"
                      stroke="#31383B"
                      stroke-width="1.25"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M16.5599 14.763L15.7266 13.9297"
                      stroke="#31383B"
                      stroke-width="1.25"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <select
                    value={filterBy}
                    onChange={(e) => setFilterBy(e.target.value)}
                  >
                    <option value="category">Filter by category</option>
                    <option value="processingTime">
                      Filter by processing time
                    </option>
                    <option value="amount">Filter by amount</option>
                  </select>
                </motion.div>
                <motion.div
                  className="sort-options"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  <motion.button
                    className={viewMode === "grid" ? "active" : ""}
                    onClick={() => setViewMode("grid")}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg
                      width="24"
                      height="25"
                      viewBox="0 0 24 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 22.5H15C20 22.5 22 20.5 22 15.5V9.5C22 4.5 20 2.5 15 2.5H9C4 2.5 2 4.5 2 9.5V15.5C2 20.5 4 22.5 9 22.5Z"
                        stroke="#6B729E"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M12 2.5V22.5"
                        stroke="#6B729E"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M2 12.5H22"
                        stroke="#6B729E"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </motion.button>
                  <motion.button
                    className={viewMode === "list" ? "active" : ""}
                    onClick={() => setViewMode("list")}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg
                      width="24"
                      height="25"
                      viewBox="0 0 24 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M19.9 14H4.1C2.6 14 2 14.64 2 16.23V20.27C2 21.86 2.6 22.5 4.1 22.5H19.9C21.4 22.5 22 21.86 22 20.27V16.23C22 14.64 21.4 14 19.9 14Z"
                        stroke="#6B729E"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M19.9 2.5H4.1C2.6 2.5 2 3.14 2 4.73V8.77C2 10.36 2.6 11 4.1 11H19.9C21.4 11 22 10.36 22 8.77V4.73C22 3.14 21.4 2.5 19.9 2.5Z"
                        stroke="#6B729E"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </motion.button>
                </motion.div>
              </div>
            </motion.div>

            <div className="tests-container">
              <AnimatePresence mode="wait">
                {viewMode === "list" ? (
                  <motion.div
                    key="list-view"
                    className="tests-table"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={pageTransition}
                  >
                    <motion.div
                      className="table-header"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                    >
                      <span>Test name</span>
                      <span>Amount</span>
                      <span>Processing time</span>
                    </motion.div>
                    <motion.div
                      className="table-body"
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      {currentTests.map((test, index) => (
                        <motion.div
                          key={test.slug}
                          className="table-row"
                          variants={rowVariants}
                          initial="hidden"
                          animate="visible"
                          whileHover="hover"
                          custom={index}
                          transition={{ delay: index * 0.03 }}
                        >
                          <span>{test.name}</span>
                          <span>{test.price}</span>
                          <span>{test.processingTime}</span>
                          <motion.button
                            className="view-details"
                            onClick={() =>
                              navigate(`/medical-test/${test.slug}`)
                            }
                            whileHover={{ color: "#222952" }}
                            whileTap={{ scale: 0.95 }}
                          >
                            View details
                          </motion.button>
                          <motion.button
                            className="order-test"
                            onClick={() =>
                              navigate(`/review-test-order?test_slug=${test.slug}`)
                            }
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                          >
                            Order test
                          </motion.button>
                        </motion.div>
                      ))}
                    </motion.div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="grid-view"
                    className="tests-grid"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit={{ opacity: 0 }}
                    transition={pageTransition}
                  >
                    {currentTests.map((test, index) => (
                      <motion.div
                        key={test.slug}
                        className="test-box"
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover="hover"
                        custom={index}
                        transition={{ delay: index * 0.05 }}
                      >
                        <motion.h3
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.1 + index * 0.05 }}
                        >
                          {test.name}
                        </motion.h3>
                        <motion.p
                          className="test-info-value"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.2 + index * 0.05 }}
                        >
                          {test.info}
                        </motion.p>
                        <motion.p
                          className="price"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.3 + index * 0.05 }}
                        >
                          {test.price}
                        </motion.p>
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.4 + index * 0.05 }}
                        >
                          Estimated processing time
                        </motion.p>
                        <motion.p
                          className="processing-time-value"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.5 + index * 0.05 }}
                        >
                          {test.processingTime}
                        </motion.p>
                        <motion.div
                          className="buttons-container"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.6 + index * 0.05 }}
                        >
                          <motion.button
                            className="order-test"
                            onClick={() =>
                              navigate(`/review-test-order?test_slug=${test.slug}`)
                            }
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                          >
                            Order test
                          </motion.button>
                          <motion.button
                            className="view-details"
                            onClick={() =>
                              navigate(`/medical-test/${test.slug}`)
                            }
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                          >
                            View test details
                          </motion.button>
                        </motion.div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <motion.div
              className="pagination"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <div className="page-selector">
                <motion.input
                  type="text"
                  value={pageInput}
                  onChange={handlePageInputChange}
                  className={parseInt(pageInput) > totalPages ? "invalid" : ""}
                  whileFocus={{
                    scale: 1.05,
                    boxShadow: "0px 0px 5px rgba(58, 63, 106, 0.2)",
                  }}
                  transition={{ duration: 0.2 }}
                />
                <span>of {totalPages} pages</span>
              </div>
              <div className="page-arrows">
                <motion.button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  whileHover={currentPage !== 1 ? { scale: 1.1 } : {}}
                  whileTap={currentPage !== 1 ? { scale: 0.9 } : {}}
                >
                  <svg
                    className="arrow-button-icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </motion.button>
                <motion.button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  whileHover={currentPage !== totalPages ? { scale: 1.1 } : {}}
                  whileTap={currentPage !== totalPages ? { scale: 0.9 } : {}}
                >
                  <svg
                    className="arrow-button-icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 6 15 12 9 18"></polyline>
                  </svg>
                </motion.button>
              </div>
              <div className="tests-per-page">
                <span>Tests per page:</span>
                <div className="select-wrapper">
                  <motion.select
                    value={testsPerPage}
                    onChange={handleTestsPerPageChange}
                    whileFocus={{ scale: 1.05 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <option value={6}>6</option>
                    <option value={7}>7</option>
                    <option value={8}>8</option>
                    <option value={9}>9</option>
                    <option value={10}>10</option>
                  </motion.select>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </div>
            </motion.div>
          </>
        ) : (
          <motion.div
            className="no-tests"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <motion.img
              src={`${process.env.PUBLIC_URL}/assets/no-tests.png`}
              alt="No tests"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.5,
                delay: 0.4,
                type: "spring",
                stiffness: 200,
              }}
            />
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              No tests available to order
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              All tests will appear here.
            </motion.p>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default OrderATest;
